import { Plus } from '@phosphor-icons/react'
import { Button, Form } from 'antd'
import { useRef } from 'react'
import { createPortal } from 'react-dom'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import { LevelType } from '@/domain/model/measures'
import { useUpdateURLSearchParams } from '@/hooks/useQuery'
import useSearchParams from '@/hooks/useSearchParams'
import { useAddNewLevel } from '@/queries/measures/useAddNewLevel'
import { useLoadMeasuresByFormAndClientId } from '@/queries/measures/useLoadMeasuresByFormAndClientId'
import FormAntd from '@/ui/form/antd/FormAntd'
import { FormDynamicFieldGroupMethods } from '@/ui/form/antd/FormDynamicFieldGroup'
import Modal from '@/ui/Modal'

import { getCurrentQuestionById } from '../../utils/getCurrentQuestionById'
import { LevelsModel, useLevelsForm } from './useLevelsForm'

export const LevelsModal = () => {
  const { clientId = '' } = useParams<{ clientId?: string }>()
  const searchParams = useSearchParams()
  const updateParams = useUpdateURLSearchParams()
  const { form, rule } = useLevelsForm()

  const formId = searchParams.get('formId') || ''
  const questionId = searchParams.get('question') || ''

  const { addNewLevel, isLoading } = useAddNewLevel({
    questionId,
    formId,
    clientId,
  })

  const dynamicGroupRef = useRef<FormDynamicFieldGroupMethods>(null)

  const { data: measureData } = useLoadMeasuresByFormAndClientId(
    formId,
    clientId || ''
  )

  const currentQuestion = getCurrentQuestionById({
    data: measureData,
    questionId,
  })

  const onFormSubmit = async (data: LevelsModel) => {
    const formattedData = data?.levels.map((item) => ({
      ...item,
      threshold: Number(item.threshold),
    })) as LevelType[]
    try {
      await addNewLevel({
        data: formattedData,
        questionId,
        formId,
      })
      toast.success('Level added successfully')
      updateParams(['assessment_levels', undefined])
    } catch (error) {
      toast.error('Error while add a level')
    }
  }

  let initialValues = {
    levels: [
      {
        color: undefined,
        threshold: undefined,
        label: undefined,
      } as unknown as LevelType,
    ],
  }

  if (currentQuestion?.levels.length) {
    initialValues = { levels: currentQuestion.levels }
  }

  return createPortal(
    <Modal
      open
      cancelButtonProps={{ disabled: isLoading }}
      okButtonProps={{ disabled: isLoading, loading: isLoading }}
      onCancel={() => updateParams(['assessment_levels', undefined])}
      onClose={() => updateParams(['assessment_levels', undefined])}
      onOk={() => form.submit()}
      title="Edit Levels"
    >
      <p className="mb-4 text-sm text-gray-500">
        Define thresholds, labels, and colors for levels
      </p>

      <FormAntd.Root<LevelsModel>
        form={form}
        initialValues={initialValues}
        layout="vertical"
        onFinish={onFormSubmit}
        validateTrigger={['onBlur']}
      >
        <FormAntd.DynamicFieldGroup
          fieldName="levels"
          initialItem={{
            color: undefined,
            threshold: undefined,
            label: undefined,
          }}
          ref={dynamicGroupRef}
          renderItem={({ field }) => (
            <div className="grid w-full grid-cols-[0.8fr_2fr_0.5fr] gap-4">
              <Form.Item
                {...field}
                className="w-full"
                label="Threshold"
                name={[field.name, 'threshold']}
                rules={[rule]}
              >
                <FormAntd.Input placeholder="Enter threshold" type="number" />
              </Form.Item>

              <Form.Item
                {...field}
                className="w-full"
                label="Label"
                name={[field.name, 'label']}
                rules={[rule]}
              >
                <FormAntd.Input placeholder="Enter label" />
              </Form.Item>
              <Form.Item
                {...field}
                className="w-full"
                label="Color"
                name={[field.name, 'color']}
                rules={[rule]}
              >
                <FormAntd.ColorSelect placeholder="Color" />
              </Form.Item>
            </div>
          )}
        />
        <Button
          block
          className="my-2 text-gray-400"
          icon={<Plus weight="bold" />}
          onClick={() => dynamicGroupRef.current?.add()}
          type="dashed"
        >
          Add New Level
        </Button>
      </FormAntd.Root>
    </Modal>,
    document.body
  )
}
